/**
 * frontend/src/pages/valuation/ValuationNormalize.tsx
 *
 * VAL-U2C-SCREEN-1 — "Review and adjust", between Financials and Intake. The
 * signer sees each year side by side, adds add-backs with a reason, types total
 * assets and liabilities, and sees the normalized earnings and the rule (each save
 * re-applies them on the server). Nothing is computed here, and nothing is shown that the
 * server did not return: every save re-reads the view (the stored value is what
 * the screen shows). No browser storage.
 *
 * VAL-PATH-ADJUST P4b — step 3 is "Adjustments and rates". The four legs moved
 * here from Intake and save on type (only the legs are sent — never earnings,
 * rates, the asset floor or answers); the rates show read-only (D5). The Apply
 * button is gone: with no draft yet, the result is applied once, just before
 * the first leg save, so the draft exists to save into. Below two years the
 * legs and Save and continue are locked; Back still goes to Financials.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { fetchValuationMethodology } from '@/api/valuation'
import { fetchValuationDraft, saveValuationDraft } from '@/api/valuationB3'
import type { DraftInputs } from '@/types/valuationDraft'
import { fetchFinancials, type FinancialsRow } from '@/api/valuationFinancials'
import {
  applyNormalization, fetchNormalization, removeAddBack, saveAddBack, saveBalanceSheet,
  type AddBackBody, type NormalizationView,
} from '@/api/valuationNormalization'
import type { ValuationMethodology } from '@/types/valuation'
import { formatApiError } from '@/utils/httpErrors'
import { ValuationStepper, basisLabel } from './_shared'
import { StepFooter } from '@/components/valuation/StepFooter'
import { NormalizeYearTable, money } from './_NormalizeYearTable'
import { NormalizeAddBacks } from './_NormalizeAddBacks'
import { NormalizeBalanceSheet } from './_NormalizeBalanceSheet'
import { AdjustLegsSection, type LegValues } from './_AdjustLegs'
import { RatesSection } from './_RatesUsed'
import { useAutosave } from './_useAutosave'
import { legsAutosavePatch, ratesFrom } from './_valuationIntakeHelpers'
import { LOCKED_MESSAGE } from './_steps'

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))

function legsFrom(d: DraftInputs | null): LegValues {
  return {
    replacementCompensation: str(d?.replacement_compensation),
    baseCashFlow: str(d?.base_cash_flow),
    compMultiples: (d?.comp_multiples ?? []).join(', '),
    fairValueAdjustments: str(d?.fair_value_adjustments),
  }
}

export default function ValuationNormalize() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const [view, setView] = useState<NormalizationView | null>(null)
  const [rows, setRows] = useState<FinancialsRow[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const [draft, setDraft] = useState<DraftInputs | null>(null)
  const [draftLoaded, setDraftLoaded] = useState(false)
  const [methodology, setMethodology] = useState<ValuationMethodology | null>(null)
  const [legs, setLegs] = useState<LegValues>(legsFrom(null))
  const draftStarted = useRef(false)

  const reload = useCallback(async (id: string) => { setView(await fetchNormalization(id)) }, [])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    Promise.all([fetchNormalization(valuationId), fetchFinancials(valuationId), fetchValuationDraft(valuationId)])
      .then(([n, f, d]) => {
        if (!live) return
        setView(n)
        setRows(f.rows)
        setDraft(d.inputs)
        setLegs(legsFrom(d.inputs))
        draftStarted.current = !!d.inputs
        setDraftLoaded(true)
      })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    // The crossover hint only; the legs work without it.
    fetchValuationMethodology().then(m => { if (live) setMethodology(m) }).catch(() => {})
    return () => { live = false }
  }, [valuationId])

  /** One server call at a time; the view is re-read after every change. */
  async function run(work: (id: string) => Promise<void>) {
    if (!valuationId || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      await work(valuationId)
    } catch (err: unknown) {
      const message = formatApiError(err)
      setError(message)
      toast.error(message)
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const add = (body: AddBackBody) => run(async id => { await saveAddBack(id, body); await reload(id) })
  const remove = (addBackId: string) => run(async id => { await removeAddBack(id, addBackId); await reload(id) })
  const saveSheet = (body: { total_assets: number; total_liabilities: number; as_of_year: number | null }) =>
    run(async id => { await saveBalanceSheet(id, body); await reload(id) })

  const locked = !view || view.years.length < 2
  const { patch: legsPatch, problems } = legsAutosavePatch(legs)
  const legsSave = useAutosave(
    legsPatch,
    async patch => {
      if (!valuationId) return null
      try {
        // No draft yet: the applied result creates it, once, before the first leg save.
        if (!draftStarted.current) { await applyNormalization(valuationId); draftStarted.current = true }
        await saveValuationDraft(valuationId, patch)
        return null
      } catch (err: unknown) { return formatApiError(err) }
    },
    { enabled: draftLoaded && !locked },
  )
  const saveLine = problems.length > 0 ? `Not saved — check ${problems.join(', ')}`
    : legsSave.status === 'error' ? `Not saved — ${legsSave.message}`
    : legsSave.status === 'saving' ? 'Saving…'
    : null

  async function stepSave() {
    if (problems.length > 0) throw new Error(`Not saved — check ${problems.join(', ')}`)
    return legsSave.saveNow()
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div role="alert" className="rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">{loadError}</div>
      </div>
    )
  }
  if (!view) return <div className="mx-auto max-w-7xl p-6 text-sm text-pal-ink-muted">Loading…</div>

  const result = 'error' in view.result ? null : view.result
  const resultError = 'error' in view.result ? view.result.error : null
  const years = [...view.years].sort((a, b) => a - b)

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <h1 className="text-2xl font-bold text-pal-ink">Adjustments and rates</h1>
      <ValuationStepper current="adjust" engagementId={valuationId} />

      {view.years.length === 0 ? (
        <p data-testid="no-statements" className="rounded-lg border border-pal-line bg-pal-white p-4 text-sm text-pal-ink-body">
          {/* WALKFIX-B F15: the approved lock line; financials may be typed or uploaded. */}
          {LOCKED_MESSAGE}{' '}
          <Link to={`/valuation/${valuationId}/financials`} className="text-pal-link underline">Go to Financials</Link>
        </p>
      ) : (
      <>
      <NormalizeYearTable rows={rows} normalized={result?.years ?? []} />
      {error && <div role="alert" className="rounded-lg border border-pal-error-border bg-pal-error-bg p-3 text-sm text-pal-error-text">{error}</div>}

      <NormalizeAddBacks
        years={years}
        addBacks={view.add_backs}
        removedCount={view.removed_count}
        busy={busy}
        onAdd={add}
        onRemove={remove}
      />
      <NormalizeBalanceSheet
        key={view.balance_sheet ? 'stored' : 'empty'}
        stored={view.balance_sheet}
        asOfYear={years[years.length - 1]}
        busy={busy}
        onSave={saveSheet}
      />

      {result && (
        <section className="space-y-2 rounded-lg border border-pal-line bg-pal-white p-4">
          <h2 className="text-sm font-semibold text-pal-ink">Result</h2>
          <p className="text-sm text-pal-ink-body">{result.rule_text}</p>
          <p className="text-sm text-pal-ink-body">Basis: {basisLabel(result.basis)}</p>
          <p className="text-lg font-semibold text-pal-ink">
            Normalized earnings: <span data-testid="normalized-earnings">{money(result.normalized_earnings)}</span>
          </p>
        </section>
      )}
      {!result && !locked && resultError && <p className="text-sm text-pal-warning-text">{resultError}</p>}

      <AdjustLegsSection
        engagementId={valuationId ?? ''}
        methodology={methodology}
        values={legs}
        onChange={(key, value) => setLegs(prev => ({ ...prev, [key]: value }))}
        locked={locked}
      />
      {saveLine && <p className="text-xs text-pal-ink-muted" role="status">{saveLine}</p>}
      <RatesSection values={ratesFrom(draft)} />

      <StepFooter step="adjust" engagementId={valuationId ?? ''} save={stepSave} nextDisabled={locked} />
      </>
      )}
    </div>
  )
}
